import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { EventDefinition, HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  Brand,
  BrandCreatedEnvelope,
  BrandDeletedEnvelope,
  BrandQuery,
  BrandUpdatedEnvelope,
  BrandsQueryBuilder,
  BulkCreateBrandsOptions,
  BulkCreateBrandsResponse,
  BulkCreateBrandsValidationErrors,
  BulkDeleteBrandsResponse,
  BulkGetOrCreateBrandsOptions,
  BulkGetOrCreateBrandsResponse,
  BulkUpdateBrandsOptions,
  BulkUpdateBrandsResponse,
  CreateBrandValidationErrors,
  GetBrandOptions,
  GetOrCreateBrandOptions,
  GetOrCreateBrandResponse,
  GetOrCreateBrandValidationErrors,
  MaskedBrand,
  QueryBrandsOptions,
  QueryBrandsResponse,
  UpdateBrand,
  UpdateBrandOptions,
  bulkCreateBrands as universalBulkCreateBrands,
  bulkDeleteBrands as universalBulkDeleteBrands,
  bulkGetOrCreateBrands as universalBulkGetOrCreateBrands,
  bulkUpdateBrands as universalBulkUpdateBrands,
  createBrand as universalCreateBrand,
  deleteBrand as universalDeleteBrand,
  getBrand as universalGetBrand,
  getOrCreateBrand as universalGetOrCreateBrand,
  queryBrands as universalQueryBrands,
  typedQueryBrands as universalTypedQueryBrands,
  updateBrand as universalUpdateBrand,
} from './stores-catalog-v3-brand-brands-v-3.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/stores' };

export function createBrand(httpClient: HttpClient): CreateBrandSignature {
  return (brand: NonNullablePaths<Brand, `name`, 2>) =>
    universalCreateBrand(
      brand,
      // @ts-ignore
      { httpClient }
    );
}

interface CreateBrandSignature {
  /**
   * Creates a brand.
   * @param - Brand to create.
   * @returns Created brand.
   */
  (brand: NonNullablePaths<Brand, `name`, 2>): Promise<
    NonNullablePaths<Brand, `name`, 2> & {
      __validationErrorsType?: CreateBrandValidationErrors;
    }
  >;
}

export function getBrand(httpClient: HttpClient): GetBrandSignature {
  return (brandId: string, options?: GetBrandOptions) =>
    universalGetBrand(
      brandId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface GetBrandSignature {
  /**
   * Retrieves a brand.
   * @param - Brand ID.
   * @returns Brand.
   */
  (brandId: string, options?: GetBrandOptions): Promise<
    NonNullablePaths<Brand, `name`, 2>
  >;
}

export function updateBrand(httpClient: HttpClient): UpdateBrandSignature {
  return (
    _id: string,
    brand: NonNullablePaths<UpdateBrand, `revision`, 2>,
    options?: UpdateBrandOptions
  ) =>
    universalUpdateBrand(
      _id,
      brand,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateBrandSignature {
  /**
   * Updates a brand.
   *
   * Each time the brand is updated, `revision` increments by 1.
   * The current `revision` must be passed when updating the brand.
   * This ensures you're working with the latest brand and prevents unintended overwrites.
   * @param - Brand ID.
   * @returns Updated brand.
   */
  (
    _id: string,
    brand: NonNullablePaths<UpdateBrand, `revision`, 2>,
    options?: UpdateBrandOptions
  ): Promise<NonNullablePaths<Brand, `name`, 2>>;
}

export function deleteBrand(httpClient: HttpClient): DeleteBrandSignature {
  return (brandId: string) =>
    universalDeleteBrand(
      brandId,
      // @ts-ignore
      { httpClient }
    );
}

interface DeleteBrandSignature {
  /**
   * Deletes a brand.
   *
   * > **Note:** Deleting a brand will also remove it from all products it is assigned to.
   * @param - Brand ID.
   */
  (brandId: string): Promise<void>;
}

export function queryBrands(httpClient: HttpClient): QueryBrandsSignature {
  return (options?: QueryBrandsOptions) =>
    universalQueryBrands(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface QueryBrandsSignature {
  /**
   * Retrieves a list of up to 100 brands, given the provided filtering, sorting, and cursor paging.
   * Pass supported values to the `fields` array in the request to include those fields in the response.
   *
   *
   * Query Brands runs with these defaults, which you can override:
   *
   * - `createdDate` is sorted in `DESC` order
   * - `cursorPaging.limit` is `100`
   *
   * To learn about working with _Query_ endpoints, see
   * [API Query Language](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-the-wix-api-query-language),
   * and [Sorting and Paging](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-sorting-and-paging).
   */
  (options?: QueryBrandsOptions): BrandsQueryBuilder;
}

export function typedQueryBrands(
  httpClient: HttpClient
): TypedQueryBrandsSignature {
  return (query: BrandQuery, options?: QueryBrandsOptions) =>
    universalTypedQueryBrands(
      query,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface TypedQueryBrandsSignature {
  /** */
  (query: BrandQuery, options?: QueryBrandsOptions): Promise<
    NonNullablePaths<QueryBrandsResponse, `brands` | `brands.${number}.name`, 4>
  >;
}

export function bulkCreateBrands(
  httpClient: HttpClient
): BulkCreateBrandsSignature {
  return (
    brands: NonNullablePaths<Brand, `name`, 2>[],
    options?: BulkCreateBrandsOptions
  ) =>
    universalBulkCreateBrands(
      brands,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkCreateBrandsSignature {
  /**
   * Creates multiple brands.
   * @param - Brands to create.
   */
  (
    brands: NonNullablePaths<Brand, `name`, 2>[],
    options?: BulkCreateBrandsOptions
  ): Promise<
    NonNullablePaths<
      BulkCreateBrandsResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `results.${number}.item.name`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    > & {
      __validationErrorsType?: BulkCreateBrandsValidationErrors;
    }
  >;
}

export function bulkUpdateBrands(
  httpClient: HttpClient
): BulkUpdateBrandsSignature {
  return (
    brands: NonNullablePaths<MaskedBrand, `brand._id` | `brand.revision`, 3>[],
    options?: BulkUpdateBrandsOptions
  ) =>
    universalBulkUpdateBrands(
      brands,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkUpdateBrandsSignature {
  /**
   * Updates multiple brands.
   *
   * Each time a brand is updated, `revision` increments by 1.
   * The current `revision` must be passed when updating a brand.
   * This ensures you're working with the latest brand and prevents unintended overwrites.
   * @param - List of brands to update.
   */
  (
    brands: NonNullablePaths<MaskedBrand, `brand._id` | `brand.revision`, 3>[],
    options?: BulkUpdateBrandsOptions
  ): Promise<
    NonNullablePaths<
      BulkUpdateBrandsResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `results.${number}.item.name`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    >
  >;
}

export function getOrCreateBrand(
  httpClient: HttpClient
): GetOrCreateBrandSignature {
  return (brandName: string, options?: GetOrCreateBrandOptions) =>
    universalGetOrCreateBrand(
      brandName,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface GetOrCreateBrandSignature {
  /**
   * Retrieves a brand by name, or creates a brand if one with the passed `brandName` doesn't exist.
   * @param - Brand name to retrieve or create.
   */
  (brandName: string, options?: GetOrCreateBrandOptions): Promise<
    NonNullablePaths<GetOrCreateBrandResponse, `brand.name`, 3> & {
      __validationErrorsType?: GetOrCreateBrandValidationErrors;
    }
  >;
}

export function bulkGetOrCreateBrands(
  httpClient: HttpClient
): BulkGetOrCreateBrandsSignature {
  return (brandNames: string[], options?: BulkGetOrCreateBrandsOptions) =>
    universalBulkGetOrCreateBrands(
      brandNames,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkGetOrCreateBrandsSignature {
  /**
   * Retrieves multiple brands by name, or creates multiple brands if those with the passed `brandNames` don't exist.
   * @param - Brand names to retrieve or create.
   */
  (brandNames: string[], options?: BulkGetOrCreateBrandsOptions): Promise<
    NonNullablePaths<
      BulkGetOrCreateBrandsResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `results.${number}.item.name`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    >
  >;
}

export function bulkDeleteBrands(
  httpClient: HttpClient
): BulkDeleteBrandsSignature {
  return (brandIds: string[]) =>
    universalBulkDeleteBrands(
      brandIds,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkDeleteBrandsSignature {
  /**
   * Deletes multiple brands.
   * @param - IDs of brands to delete.
   */
  (brandIds: string[]): Promise<
    NonNullablePaths<
      BulkDeleteBrandsResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    >
  >;
}

export const onBrandCreated = EventDefinition(
  'wix.stores.catalog.v3.brand_created',
  true,
  (event: BrandCreatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<BrandCreatedEnvelope>();
export const onBrandDeleted = EventDefinition(
  'wix.stores.catalog.v3.brand_deleted',
  true,
  (event: BrandDeletedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'undefined.createdDate' },
            { path: 'undefined.updatedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<BrandDeletedEnvelope>();
export const onBrandUpdated = EventDefinition(
  'wix.stores.catalog.v3.brand_updated',
  true,
  (event: BrandUpdatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'metadata.eventTime' },
            { path: 'modifiedFields.createdDate' },
            { path: 'modifiedFields.updatedDate' },
          ],
        },
      ])
    )
)<BrandUpdatedEnvelope>();

export {
  AccountInfo,
  AccountInfoMetadata,
  ActionEvent,
  App,
  ApplicationError,
  BaseEventMetadata,
  Brand,
  BrandCreatedEnvelope,
  BrandDeletedEnvelope,
  BrandQuerySpec,
  BrandUpdatedEnvelope,
  BrandsQueryBuilder,
  BrandsQueryResult,
  BulkActionMetadata,
  BulkBrandsResult,
  BulkCreateBrandsOptions,
  BulkCreateBrandsRequest,
  BulkCreateBrandsResponse,
  BulkDeleteBrandsRequest,
  BulkDeleteBrandsResponse,
  BulkDeleteBrandsResponseBulkBrandsResult,
  BulkGetOrCreateBrandsOptions,
  BulkGetOrCreateBrandsRequest,
  BulkGetOrCreateBrandsResponse,
  BulkUpdateBrandsOptions,
  BulkUpdateBrandsRequest,
  BulkUpdateBrandsResponse,
  CreateBrandRequest,
  CreateBrandResponse,
  CursorPaging,
  CursorPagingMetadata,
  CursorQuery,
  CursorQueryPagingMethodOneOf,
  Cursors,
  CustomTag,
  DeleteBrandRequest,
  DeleteBrandResponse,
  DomainEvent,
  DomainEventBodyOneOf,
  Empty,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  EventMetadata,
  File,
  GetBrandOptions,
  GetBrandRequest,
  GetBrandResponse,
  GetOrCreateBrandOptions,
  GetOrCreateBrandRequest,
  GetOrCreateBrandResponse,
  IdentificationData,
  IdentificationDataIdOneOf,
  InvalidateCache,
  InvalidateCacheGetByOneOf,
  ItemMetadata,
  MaskedBrand,
  MessageEnvelope,
  Page,
  Pages,
  QueryBrandsOptions,
  QueryBrandsRequest,
  QueryBrandsResponse,
  RecloneSiteRequest,
  RecloneSiteResponse,
  RequestedFields,
  RestoreInfo,
  SortOrder,
  Sorting,
  URI,
  URIs,
  UpdateBrand,
  UpdateBrandOptions,
  UpdateBrandRequest,
  UpdateBrandResponse,
  WebhookIdentityType,
  utils,
} from './stores-catalog-v3-brand-brands-v-3.universal.js';
